import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ensureGuestInvoice, getInvoice, listAdminInvoices, listGuestInvoices, openInvoicePdf } from '../api/invoices';
import { useCurrency } from '../components/CurrencyContext';
import { getLocalItem, getSessionItem } from '../utils/safeStorage';
import { sanitizeCustomerInvoiceLineItems } from '../../utils/customerPricingVisibility';

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const InvoicePage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [invoice, setInvoice] = useState(null);
  const [accessRole, setAccessRole] = useState('guest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const readToken = (key) => getSessionItem(key) || getLocalItem(key) || '';

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const isAuthError = (err) => /unauthorized|forbidden|invalid|expired/i.test(String(err?.message || ''));
        let authIssue = false;

        try {
          const list = await listGuestInvoices({ bookingId, limit: 1 });
          let entry = list?.data?.[0];
          if (!entry?.id) {
            entry = await ensureGuestInvoice(bookingId);
          }
          if (entry) {
            const detail = await getInvoice(entry.id, null, { asGuest: true });
            if (!active) return;
            setAccessRole('guest');
            setInvoice(detail);
            return;
          }
        } catch (err) {
          if (isAuthError(err)) {
            authIssue = true;
          } else {
            throw err;
          }
        }

        try {
          const adminToken = readToken('adminAccessToken') || undefined;
          const list = await listAdminInvoices({ bookingId, type: 'GUEST_RECEIPT', limit: 1 }, adminToken);
          const entry = list?.data?.[0];
          if (entry) {
            const detail = await getInvoice(entry.id, adminToken);
            if (!active) return;
            setAccessRole('admin');
            setInvoice(detail);
            return;
          }
        } catch (err) {
          if (isAuthError(err)) {
            authIssue = true;
          } else {
            throw err;
          }
        }

        if (!active) return;
        if (authIssue) {
          setError('Please sign in to view this invoice.');
          return;
        }
        setError('Invoice not available yet. Please check your dashboard shortly.');
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Failed to load invoice');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (bookingId) {
      load();
    }
    return () => {
      active = false;
    };
  }, [bookingId]);

  const currency = invoice?.currency || 'INR';
  const totals = invoice?.totals || {};
  const lineItems = useMemo(
    () => (accessRole === 'admin' ? invoice?.lineItems || [] : sanitizeCustomerInvoiceLineItems(invoice?.lineItems || [])),
    [accessRole, invoice]
  );
  const bookingKey = invoice?.stayDetails?.bookingCode || bookingId;
  const invoicesPath = accessRole === 'admin' ? '/admin/invoices' : '/guest/invoices';

  const money = (value) => {
    const formatted = formatCurrency(value, currency);
    if (formatted) return formatted;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${currency} ${value || 0}`.trim();
    return `${currency} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
  };

  const handleDownload = async () => {
    if (!invoice?.id) return;
    try {
      const token = accessRole === 'admin' ? readToken('adminAccessToken') : '';
      await openInvoicePdf(invoice.id, token, { asGuest: accessRole !== 'admin', download: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') return;
      setError(err.message || 'Unable to open invoice');
    }
  };

  const handlePrint = async () => {
    if (!invoice?.id) return;
    try {
      const token = accessRole === 'admin' ? readToken('adminAccessToken') : '';
      await openInvoicePdf(invoice.id, token, { asGuest: accessRole !== 'admin', print: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') return;
      setError(err.message || 'Unable to print invoice');
    }
  };

  if (loading) return <div className="p-6 text-slate-600">Loading invoice...</div>;
  if (error) {
    const friendly = /unauthorized|forbidden|invalid|expired/i.test(error)
      ? 'Please sign in to view this invoice.'
      : error;
    return <div className="p-6 text-red-600">{friendly}</div>;
  }
  if (!invoice) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Link to="/">Home</Link>
          <span>/</span>
          <span className="text-slate-700">Invoice</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-outline-secondary" onClick={handleDownload}>
            Download PDF
          </button>
          <button className="btn btn-outline-secondary" onClick={handlePrint}>
            Print
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6 border border-slate-100 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Invoice</p>
            <h2 className="text-2xl font-semibold text-slate-900">{invoice.invoiceNumber}</h2>
            <p className="text-sm text-slate-600">
              {invoice.invoiceType?.replace(/_/g, ' ')} · Issued {formatDate(invoice.issuedAt)}
            </p>
          </div>
          <div className="text-sm text-slate-700">
            <div className="font-semibold text-slate-900">{invoice.issuedBy?.name || 'Homevilas'}</div>
            <div>{invoice.issuedBy?.email || 'info@homevilas.com, meena@homevilas.com'}</div>
            <div>{invoice.issuedBy?.phone || '+918287703118'}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Billed to</p>
            <p className="text-sm text-slate-900">{invoice.billedTo?.name || 'Guest'}</p>
            <p className="text-sm text-slate-700">{invoice.billedTo?.email || '-'}</p>
            <p className="text-sm text-slate-700">{invoice.billedTo?.phone || '-'}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Stay details</p>
            <p className="text-sm text-slate-900">{invoice.stayDetails?.propertyName || 'Homevilas stay'}</p>
            <p className="text-sm text-slate-700">{invoice.stayDetails?.propertyLocation || '-'}</p>
            <p className="text-sm text-slate-700">
              {formatDate(invoice.stayDetails?.checkIn)} - {formatDate(invoice.stayDetails?.checkOut)}
            </p>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">Charges</div>
          <div className="divide-y">
            {lineItems.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500">No line items available.</div>
            )}
            {lineItems.map((item, idx) => (
              <div key={`${item.description}-${idx}`} className="flex justify-between px-3 py-2 text-sm text-slate-800">
                <span>{item.description}</span>
                <span>{money(item.amount ?? 0)}</span>
              </div>
            ))}
            <div className="flex justify-between px-3 py-3 text-sm font-semibold text-slate-900 bg-slate-50">
              <span>Total</span>
              <span>{money(totals.grandTotal ?? totals.total ?? 0)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Payment</p>
            <p className="text-sm text-slate-700">Provider: {invoice.paymentInfo?.provider || '-'}</p>
            <p className="text-sm text-slate-700">Transaction: {invoice.paymentInfo?.transactionId || '-'}</p>
            <p className="text-sm text-slate-700">Paid at: {formatDate(invoice.paymentInfo?.paidAt)}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Quick links</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate(invoicesPath)}
              >
                Go to invoices
              </button>
              <Link className="btn btn-outline-secondary btn-sm" to={`/confirmation/${bookingKey}`}>
                Booking confirmation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
